/**
 * CEW site-chrome constants — the single owner of every
 * cew.georgetown.edu URL/label used by `CewSiteNav`, `CewSiteFooter`,
 * `MobileMenuDrawer`'s CEW nav group, and `Topbar`'s logo/search
 * links (CR-027..030).
 *
 * Source: `.claude/visual-fixes/_history/CEW-CHROME-PARITY-SPEC-2026-08-17.md`,
 * measured live from `https://cew.georgetown.edu/` with Playwright
 * (Chromium) on 2026-08-17.
 *
 * The tool is embedded as a 100%x100% iframe on a blank WordPress page
 * (DEC-029), so every link back to the CEW site must carry
 * `target="_top"` (CEW_LINK_TARGET) — a plain link would load
 * cew.georgetown.edu *inside* the iframe instead of replacing the page.
 */

export const CEW_SITE_URL = 'https://cew.georgetown.edu';

/** `_top`, not `_blank` — escapes the iframe into the same window
 *  (Frank, 2026-08-17: "in the same window"). */
export const CEW_LINK_TARGET = '_top';

export const CEW_SEARCH_URL = 'https://cew.georgetown.edu/?s=';

/**
 * CR-034/035 (Frank, 2026-08-28): the published report + press-release
 * PDFs — single owner for every "Download …" CTA (SectionNav strip, mobile
 * drawer footer, homepage rail). PDFs open in a NEW window (`_blank`, via
 * `PdfDownloadButton`), not `CEW_LINK_TARGET`. Verified live 2026-08-30.
 */
export const CEW_REPORT_PDF_URL =
  'https://cew.georgetown.edu/wp-content/uploads/cew-springboards_to_opportunity.pdf';
export const CEW_PRESS_RELEASE_PDF_URL =
  'https://cew.georgetown.edu/wp-content/uploads/GeorgetownCEW_Press-Release_Springboards-to-Opportunity_09-02-26.pdf';

/**
 * CR-037 (Frank email 2026-08-31): the report's landing PAGE on
 * cew.georgetown.edu — used by the Methodology citation. A CEW *page*, so
 * it navigates with `CEW_LINK_TARGET` (`_top`), unlike the PDF constants
 * above.
 *
 * LIVENESS: the slug is verbatim from Frank's email, but the page returned
 * 404 when probed pre-launch on 2026-09-01 — it is the launch-day URL that
 * goes live WITH the report on Wed 2026-09-02, the same day the tool
 * releases. Deliberate: the published citation must carry the final URL.
 * Re-verify (curl -sI) as part of the 09-02 post-deploy check.
 */
export const CEW_REPORT_PAGE_URL = 'https://cew.georgetown.edu/springboards-to-opportunity';

/** The date this spec's measurements were taken — recorded so a future
 *  re-measurement pass has a clear "stale as of" marker. */
export const CEW_SNAPSHOT_DATE = '2026-08-17';

/** A single child link inside a CEW nav item's dropdown submenu. */
export interface CewNavChild {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/** A top-level CEW site nav item. Items with `children` render a
 *  disclosure chevron alongside their own landing-page link. The
 *  `variant: 'button'` item (Subscribe) renders as a pill CTA. */
export interface CewNavItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly children?: readonly CewNavChild[];
  readonly variant?: 'button';
}

/** CEW's own top nav, measured order left -> right.
 *
 *  Hrefs re-verified against the cew.georgetown.edu homepage menu on
 *  2026-10-23 (CR-046): CEW restructured its site, and `/publications/`
 *  began redirecting through `/resources` to a single Medium post. Pinned
 *  by cew-site.test.ts; `scripts/check-cew-links.mjs` flags any redirect. */
export const CEW_NAV_ITEMS: readonly CewNavItem[] = [
  {
    id: 'research',
    label: 'Research',
    href: `${CEW_SITE_URL}/research/`,
  },
  {
    id: 'resource-library',
    label: 'Resource Library',
    href: `${CEW_SITE_URL}/resource-library/`,
    children: [
      { id: 'book-chapter', label: 'Book / Chapter', href: `${CEW_SITE_URL}/book-chapter/` },
      { id: 'video', label: 'Video', href: `${CEW_SITE_URL}/video/` },
      { id: 'newsletter', label: 'Newsletter', href: `${CEW_SITE_URL}/newsletter/` },
    ],
  },
  {
    id: 'press-room',
    label: 'Press Room',
    href: `${CEW_SITE_URL}/press-room/`,
    children: [
      { id: 'press-release', label: 'Press Release', href: `${CEW_SITE_URL}/press-release/` },
      { id: 'media-mentions', label: 'Media Mentions', href: `${CEW_SITE_URL}/press-room/media-mentions/` },
      { id: 'media-inquiries', label: 'Media Inquiries', href: `${CEW_SITE_URL}/press-room/media-inquiries/` },
    ],
  },
  {
    id: 'blog',
    label: 'Blog',
    href: `${CEW_SITE_URL}/blog/`,
  },
  {
    id: 'about-cew',
    label: 'About CEW',
    href: `${CEW_SITE_URL}/about-us/`,
    children: [
      { id: 'staff', label: 'Staff', href: `${CEW_SITE_URL}/about-us/staff/` },
      { id: 'faqs', label: 'FAQs', href: `${CEW_SITE_URL}/about-us/faqs/` },
      { id: 'contact-us', label: 'Contact Us', href: `${CEW_SITE_URL}/about-us/contact-us/` },
      { id: 'careers', label: 'Careers', href: `${CEW_SITE_URL}/about-us/careers/` },
    ],
  },
  {
    id: 'subscribe',
    label: 'Subscribe',
    href: `${CEW_SITE_URL}/newsletter-signup/`,
    variant: 'button',
  },
];

/** A footer nav link. */
export interface CewFooterLink {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/** CEW's global footer nav — About CEW / Privacy Policy / EIT
 *  accessibility (lower-case "a", exactly as measured). */
export const CEW_FOOTER_LINKS: readonly CewFooterLink[] = [
  { id: 'about-cew', label: 'About CEW', href: `${CEW_SITE_URL}/about-us/` },
  { id: 'privacy-policy', label: 'Privacy Policy', href: 'https://www.georgetown.edu/privacy-policy/' },
  { id: 'eit-accessibility', label: 'EIT accessibility', href: 'https://accessibility.georgetown.edu/' },
];

/** A social-network footer link. */
export interface CewSocialLink {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/** CEW's 5 social links, measured order. */
export const CEW_SOCIAL_LINKS: readonly CewSocialLink[] = [
  { id: 'x', label: 'X', href: 'https://x.com/GeorgetownCEW' },
  { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/GeorgetownCEW' },
  { id: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/company/georgetowncew' },
  { id: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/georgetowncew' },
  { id: 'medium', label: 'Medium', href: 'https://medium.com/georgetown-cew' },
];
